import {Injectable, inject} from '@angular/core';
// eslint-disable-next-line import/no-duplicates
import {DataSnapshot} from '@firebase/database-types';
import {Reference} from '@firebase/database-types';
import {Observable, fromEvent, of} from 'rxjs';
import {map, shareReplay, switchMap} from 'rxjs/operators';
import {FirebaseDataService} from 'src/app/editor-state/firebase-data.service';
import {
  AIDVGenerationStatus,
  AIRecommendationsGenerationProgress,
  AIStoryGenerationStatus
} from 'src/app/shared/models/generation-status.interface';
import {environment} from 'src/environments/environment';

export interface TypedDataSnapshot<T> extends DataSnapshot {
  val: () => T;
}

function getGenerationStatusFromFirebaseRef<T>(
  ref: Reference | null,
  refsInitialized: boolean
): Observable<T | null> {
  if (refsInitialized && ref && !environment.NOUI) {
    return fromEvent<unknown>(ref, 'value').pipe(map(([dataSnapshot]) => dataSnapshot?.val?.()));
  }

  return of(null);
}

@Injectable()
export class GenerationProgressService {
  private firebaseDataService = inject(FirebaseDataService);
  public storyGenerationStatus$ = this.firebaseDataService.refsInitialized$.pipe(
    switchMap(refsInitialized =>
      getGenerationStatusFromFirebaseRef<AIStoryGenerationStatus>(
        this.firebaseDataService.diagramAIGenerationRef,
        refsInitialized
      )
    )
  );
  public dvGenerationStatus$ = this.firebaseDataService.refsInitialized$.pipe(
    switchMap(refsInitialized =>
      getGenerationStatusFromFirebaseRef<AIDVGenerationStatus>(
        this.firebaseDataService.dvGenerationRef,
        refsInitialized
      )
    ),
    shareReplay()
  );
  public dvGenerationInProgress$ = this.dvGenerationStatus$.pipe(map(status => Boolean(status)));

  public recommendationsUPNGenerationProgress$ = this.firebaseDataService.refsInitialized$.pipe(
    switchMap(refsInitialized =>
      getGenerationStatusFromFirebaseRef<AIRecommendationsGenerationProgress>(
        this.firebaseDataService.diagramUPNRecommendationsGenerationRef,
        refsInitialized
      )
    ),
    shareReplay()
  );

  public recommendationsAgentInstructionsGenerationProgress$ =
    this.firebaseDataService.refsInitialized$.pipe(
      switchMap(refsInitialized =>
        getGenerationStatusFromFirebaseRef<AIRecommendationsGenerationProgress>(
          this.firebaseDataService.diagramAgentInstructionsRecommendationsGenerationRef,
          refsInitialized
        )
      ),
      shareReplay()
    );

  public recommendationsAgentFinderGenerationProgress$ =
    this.firebaseDataService.refsInitialized$.pipe(
      switchMap(refsInitialized =>
        getGenerationStatusFromFirebaseRef<AIRecommendationsGenerationProgress>(
          this.firebaseDataService.diagramAgentFinderRecommendationsGenerationRef,
          refsInitialized
        )
      ),
      shareReplay()
    );
}
